import {
  declareAnnotationBundleImport,
  duplicateAnnotationBundleUuid,
  finalizeAnnotationBundleImport,
  type AnnotationBundleImportView,
  type AnnotationBundleUploadTicket,
} from "@/api/trainingAnnotation";
import type { Lifecycle } from "@/lifecycle/Lifecycle";
import {
  selectAnnotationImportBundle,
  type AnnotationBundleAnalysis,
} from "@/modules/dataset/public";
import { parseUtcMs } from "@/shared/utils/datetime/public";
import type { FolderScanResult, ScannedFile } from "@/upload";
import { PutFileHttpError, putFileToSignedUrl } from "@/upload/putFile";
import { AnnotationImportInputError, annotationImportErrorText } from "./annotationImportError";

type ImportStatus =
  | "idle"
  | "scanning"
  | "ready"
  | "declaring"
  | "uploading"
  | "finalizing"
  | "complete"
  | "error";
export type AnnotationImportSnapshot = Readonly<{
  status: ImportStatus;
  selection: Readonly<{
    rootName: string | null;
    prefix: string;
    analysis: AnnotationBundleAnalysis;
    rejectedCount: number;
  }> | null;
  completedFiles: number;
  uploadedBytes: number;
  error: string | null;
  result: Readonly<{ setUuid: string; duplicate: boolean; warnings: readonly string[] }> | null;
}>;
export type AnnotationImportDependencies = {
  declare: typeof declareAnnotationBundleImport;
  finalize: typeof finalizeAnnotationBundleImport;
  put: typeof putFileToSignedUrl;
  now: () => number;
};
const dependencies: AnnotationImportDependencies = {
  declare: declareAnnotationBundleImport,
  finalize: finalizeAnnotationBundleImport,
  put: putFileToSignedUrl,
  now: Date.now,
};
const emptySnapshot = (): AnnotationImportSnapshot => ({
  status: "idle",
  selection: null,
  completedFiles: 0,
  uploadedBytes: 0,
  error: null,
  result: null,
});
export function annotationImportBusy(status: ImportStatus): boolean {
  return status === "declaring" || status === "uploading" || status === "finalizing";
}

/** 모달 수명 동안 선택 파일, 티켓, 전송 진행을 소유한다. 새 선택과 dispose 는 이전 응답을 무효로 만든다. */
export class AnnotationBundleImportController implements Lifecycle {
  private active = false;
  private generation = 0;
  private abort: AbortController | null = null;
  private readonly listeners = new Set<() => void>();
  private snapshot = emptySnapshot();
  private files: readonly ScannedFile[] = [];
  private declaration: AnnotationBundleImportView | null = null;
  private readonly completed = new Set<string>();
  private readonly progress = new Map<string, number>();

  constructor(
    private readonly datasetUuid: string,
    private readonly cameras: ReadonlySet<string>,
    private readonly deps: AnnotationImportDependencies = dependencies,
  ) {}

  getSnapshot = (): AnnotationImportSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  init(): void {
    this.active = true;
  }
  dispose(): void {
    this.active = false;
    this.generation += 1;
    this.abort?.abort();
    this.abort = null;
    this.files = [];
    this.declaration = null;
    this.completed.clear();
    this.progress.clear();
    this.snapshot = emptySnapshot();
    this.listeners.clear();
  }

  async selectFolder(scan: () => FolderScanResult | Promise<FolderScanResult>): Promise<void> {
    if (!this.active || annotationImportBusy(this.snapshot.status)) return;
    const generation = ++this.generation;
    this.files = [];
    this.declaration = null;
    this.completed.clear();
    this.progress.clear();
    this.publish({ ...emptySnapshot(), status: "scanning" });
    try {
      const selected = await scan();
      if (!this.current(generation)) return;
      const bundle = selectAnnotationImportBundle(selected.files, this.cameras);
      const rejectedCount = selected.rejected.filter(
        (file) => file.reason !== "os_junk" && file.rawPath.startsWith(bundle.prefix),
      ).length;
      this.files = bundle.files;
      this.publish({
        ...this.snapshot,
        status: "ready",
        selection: {
          rootName: selected.rootName,
          prefix: bundle.prefix,
          analysis: bundle.analysis,
          rejectedCount,
        },
      });
    } catch (error) {
      if (this.current(generation))
        this.publish({
          ...this.snapshot,
          status: "error",
          error: annotationImportErrorText(error),
        });
    }
  }

  async start(name: string | null): Promise<void> {
    const selection = this.snapshot.selection;
    if (
      !this.active ||
      annotationImportBusy(this.snapshot.status) ||
      this.snapshot.status === "complete" ||
      selection === null ||
      selection.analysis.issues.length > 0 ||
      selection.rejectedCount > 0
    )
      return;
    const generation = this.generation;
    const abort = new AbortController();
    this.abort = abort;
    this.publish({ ...this.snapshot, error: null });
    try {
      if (this.needsDeclaration()) {
        this.declaration = null;
        this.completed.clear();
        this.progress.clear();
        this.publish({
          ...this.snapshot,
          status: "declaring",
          uploadedBytes: 0,
          completedFiles: 0,
        });
        const declared = await this.deps.declare(
          this.datasetUuid,
          this.files.map((file) => ({
            relative_path: file.relativePath,
            size_bytes: file.sizeBytes,
          })),
          { signal: abort.signal },
        );
        if (!this.current(generation)) return;
        this.validateDeclaration(declared);
        this.declaration = declared;
      }
      const declaration = this.declaration;
      if (declaration === null) return;
      this.publish({ ...this.snapshot, status: "uploading" });
      const tickets = new Map(declaration.files.map((ticket) => [ticket.relative_path, ticket]));
      const pending = this.files.filter((file) => !this.completed.has(file.relativePath));
      for (let start = 0; start < pending.length; start += 6) {
        const results = await Promise.allSettled(
          pending
            .slice(start, start + 6)
            .map((file) =>
              this.upload(file, tickets.get(file.relativePath)!, generation, abort.signal),
            ),
        );
        if (!this.current(generation)) return;
        const failure = results.find((result) => result.status === "rejected");
        if (failure?.status === "rejected") throw failure.reason;
      }
      this.publish({ ...this.snapshot, status: "finalizing" });
      const finalized = await this.deps.finalize(
        this.datasetUuid,
        declaration.set_uuid,
        // 서버는 이 본문에 NFC 가 아닌 문자열이 있으면 요청을 422 로 거부한다. 경로는 스캔에서 이미
        // NFC 다. 붙여 넣은 이름은 NFD 일 수 있다.
        {
          name: name === null ? null : name.normalize("NFC"),
          files: this.files.map((file) => file.relativePath),
        },
        { signal: abort.signal },
      );
      if (this.current(generation))
        this.publish({
          ...this.snapshot,
          status: "complete",
          result: { setUuid: finalized.set.uuid, duplicate: false, warnings: finalized.warnings },
        });
    } catch (error) {
      if (!this.current(generation)) return;
      const duplicate = duplicateAnnotationBundleUuid(error);
      if (duplicate !== null)
        this.publish({
          ...this.snapshot,
          status: "complete",
          result: { setUuid: duplicate, duplicate: true, warnings: [] },
        });
      else
        this.publish({
          ...this.snapshot,
          status: "error",
          error: annotationImportErrorText(error),
        });
    } finally {
      if (this.abort === abort) this.abort = null;
    }
  }

  private needsDeclaration(): boolean {
    if (this.declaration === null) return true;
    // 모든 파일을 전송한 뒤에는 URL 만료와 관계없이 같은 UUID 의 finalize 를 재시도한다.
    return this.declaration.files.some(
      (ticket) =>
        !this.completed.has(ticket.relative_path) &&
        (parseUtcMs(ticket.expires_at) ?? 0) <= this.deps.now() + 5_000,
    );
  }

  private validateDeclaration(declared: AnnotationBundleImportView): void {
    const paths = new Set(this.files.map((file) => file.relativePath));
    if (!declared.set_uuid || declared.files.length !== paths.size)
      throw new AnnotationImportInputError(
        "업로드할 파일의 전송 정보를 확인하지 못했습니다. 다시 시도해 주세요.",
      );
    for (const ticket of declared.files) {
      if (
        ticket.method !== "PUT" ||
        !paths.delete(ticket.relative_path) ||
        !Number.isFinite(parseUtcMs(ticket.expires_at))
      )
        throw new AnnotationImportInputError(
          "번들 전송 정보가 선택한 파일과 다릅니다. 다시 시도해 주세요.",
        );
    }
  }

  private async upload(
    file: ScannedFile,
    ticket: AnnotationBundleUploadTicket,
    generation: number,
    signal: AbortSignal,
  ): Promise<void> {
    try {
      await this.deps.put(ticket.upload_url, file.file, {
        signal,
        headers: ticket.headers,
        timeoutMs: 300_000,
        onProgress: (loaded) => {
          if (!this.current(generation)) return;
          this.progress.set(file.relativePath, Math.min(file.sizeBytes, Math.max(0, loaded)));
          this.publishProgress();
        },
      });
    } catch (error) {
      if (!this.current(generation)) return;
      // 이전 PUT 의 응답만 유실되었을 수 있다. 412 는 덮어쓰지 않고 최종 저장소 검증이 판정하게 한다.
      if (!(error instanceof PutFileHttpError && error.status === 412)) {
        if (error instanceof PutFileHttpError && (error.status === 401 || error.status === 403))
          this.declaration = null;
        throw error;
      }
    }
    if (!this.current(generation)) return;
    this.completed.add(file.relativePath);
    this.progress.set(file.relativePath, file.sizeBytes);
    this.publishProgress();
  }

  private publishProgress(): void {
    this.publish({
      ...this.snapshot,
      completedFiles: this.completed.size,
      uploadedBytes: [...this.progress.values()].reduce((sum, bytes) => sum + bytes, 0),
    });
  }
  private current(generation: number): boolean {
    return this.active && this.generation === generation;
  }
  private publish(snapshot: AnnotationImportSnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener();
  }
}
